import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import MaterialDropdown from '../components/MaterialDropdown';
import { validateEmail } from '../utils/emailService';
import { RegistrationData } from '../types/registration';
import { registerUser, getAuthErrorMessage, AuthErrorInfo, getSubscriptionLimits } from '../services/authService';
import { createCheckoutSession, verifyCheckoutSession, syncSubscriptionStatus } from '../services/stripeService';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import { logger } from '../utils/logger';
import { EngineeringLoadingScreen } from '../components/EngineeringLoadingScreen';
import '../../public/styles/main.css';

interface RegistrationPageProps {
    onRegistration: (data: RegistrationData) => void;
    onBackToLogin?: () => void;
    onContactUs?: () => void;
}

interface RegistrationFormData {
    nickname: string;
    email: string;
    password: string;
    repassword: string;
    organization: string;
    role: string;
    selectedPlan: string;
}

interface RegistrationErrors {
    nickname?: string;
    email?: string;
    password?: string;
    repassword?: string;
    organization?: string;
    role?: string;
    selectedPlan?: string;
    organizationLogo?: string;
}

export default function RegistrationPage({ onRegistration, onBackToLogin, onContactUs }: RegistrationPageProps) {
    const { strings, language } = useLanguage();
    const navigate = useNavigate();
    const [ searchParams ] = useSearchParams();

    // Check if we're returning from successful payment BEFORE first render
    // Memoize to prevent recalculation on every render
    const sessionIdParam: string | null = useMemo(() => searchParams.get('session_id'), [ searchParams ]);
    const paymentSuccessParam: string | null = useMemo(() => searchParams.get('payment_success'), [ searchParams ]);
    const isReturningFromPayment: boolean = useMemo(() => {
        return paymentSuccessParam === 'true' && !!sessionIdParam;
    }, [ paymentSuccessParam, sessionIdParam ]);

    const [ formData, setFormData ] = useState<RegistrationFormData>({
        nickname: '',
        email: '',
        password: '',
        repassword: '',
        organization: '',
        role: '',
        selectedPlan: ''
    });

    const [ errors, setErrors ] = useState<RegistrationErrors>({});
    const [ isLoading, setIsLoading ] = useState<boolean>(false);
    // Set initial state to true if returning from payment - prevents form from showing
    const [ isProcessingPayment, setIsProcessingPayment ] = useState<boolean>(isReturningFromPayment);
    const [ invitationId ] = useState<string | null>(null);
    const [ invitationInfo ] = useState<string>('');
    const [ submitStatus, setSubmitStatus ] = useState<'idle' | 'success' | 'error'>('idle');
    const [ firebaseError, setFirebaseError ] = useState<string>('');
    const [ organizationImage, setOrganizationImage ] = useState<File | null>(null);
    const [ organizationImagePreview, setOrganizationImagePreview ] = useState<string | null>(null);
    const [ useDefaultLogo, setUseDefaultLogo ] = useState<boolean>(false);
    const organizationLogoInputRef = useRef<HTMLInputElement | null>(null);

    // Security: Password re-entry modal state (shown after returning from Stripe payment)
    const [ showPasswordModal, setShowPasswordModal ] = useState<boolean>(false);
    const [ modalPassword, setModalPassword ] = useState<string>('');
    const [ modalRepassword, setModalRepassword ] = useState<string>('');
    const [ modalPasswordError, setModalPasswordError ] = useState<string>('');
    const [ modalLoading, setModalLoading ] = useState<boolean>(false); // Separate loading state for modal
    const [ isRegistrationComplete, setIsRegistrationComplete ] = useState<boolean>(false); // Flag to prevent showing form after successful registration
    const [ pendingRegistrationData, setPendingRegistrationData ] = useState<{
        nickname: string;
        email: string;
        organization: string;
        role: string;
        selectedPlan?: string;
        language: string;
        organizationImage: File | null;
        useDefaultLogo: boolean;
        subscriptionId?: string | null;
    } | null>(null);

    // Complete registration after successful payment
    const completeRegistration = useCallback(async (registrationData: {
        nickname: string;
        email: string;
        password: string;
        organization: string;
        role: string;
        selectedPlan?: string;
        language: string;
        organizationImage: File | null;
        useDefaultLogo: boolean;
    }, subscriptionId?: string | null) => {
        // Keep processing payment screen visible - don't change status
        setIsLoading(true);
        // Don't set submitStatus or firebaseError - keep loading screen

        try {
            // Register user with Firebase
            const authUser = await registerUser({
                nickname: registrationData.nickname,
                email: registrationData.email,
                password: registrationData.password,
                organization: registrationData.organization,
                role: registrationData.role,
                selectedPlan: registrationData.selectedPlan,
                language: registrationData.language,
                organizationLogoFile: registrationData.useDefaultLogo ? null : registrationData.organizationImage,
                useDefaultLogo: registrationData.useDefaultLogo
            });

            logger.log('User registered successfully:', authUser);

            // If subscriptionId is available, sync subscription status immediately
            // This ensures subscription fields are set right after registration
            if (subscriptionId && registrationData.role === 'manager') {
                try {
                    logger.log('[Registration] Syncing subscription status after registration...', { subscriptionId });
                    await syncSubscriptionStatus(subscriptionId);
                    logger.log('[Registration] Subscription status synced successfully');
                } catch (syncError) {
                    // Don't fail registration if sync fails - webhooks will update it later
                    logger.warn('[Registration] Failed to sync subscription status, webhooks will update it:', syncError);
                }
            }

            // Redirect IMMEDIATELY before any callbacks that might trigger re-renders
            // This prevents the registration form from showing even for a moment
            const redirectPath = registrationData.role === 'manager' ? '/dashboard' : '/home';
            // Use replace for immediate redirect - this will stop all React rendering
            window.location.replace(window.location.origin + redirectPath);

            // Prepare registration data for the parent component (won't execute due to redirect)
            const regData: RegistrationData = {
                nickname: registrationData.nickname,
                email: registrationData.email,
                password: registrationData.password,
                organization: registrationData.organization,
                role: registrationData.role,
                selectedPlan: registrationData.selectedPlan,
                language: registrationData.language
            };

            // Call parent callback (may not execute due to redirect, but that's OK)
            // The redirect happens first, so this won't cause re-renders
            onRegistration(regData);

            // Return resolved promise immediately
            return Promise.resolve();
        } catch (err) {
            logger.error('Firebase registration error:', err);
            const authError = err as AuthErrorInfo;
            // Only show error if not processing payment (shouldn't happen, but safety check)
            if (!isProcessingPayment) {
                setFirebaseError(getAuthErrorMessage(authError.code, strings));
                setSubmitStatus('error');
            }
            setIsProcessingPayment(false);
            setIsLoading(false);
            throw err;
        }
    }, [ onRegistration, strings, navigate ]);

    // Handle return from Stripe checkout
    useEffect(() => {
        // Use the params we already got (to avoid re-reading)
        const sessionId: string | null = sessionIdParam;
        const paymentSuccess: string | null = paymentSuccessParam;

        // Don't remove URL params immediately - we need them for processing
        // They'll be removed after redirect

        if (paymentSuccess === 'true' && sessionId) {
            logger.log('[Registration] Payment success callback detected:', {
                sessionId: sessionId,
                paymentSuccess: paymentSuccess
            });

            // Show payment processing screen
            setIsProcessingPayment(true);
            setIsLoading(true);
            setFirebaseError('');
            logger.log('[Registration] Verifying checkout session...');

            verifyCheckoutSession(sessionId).then((verificationResult) => {
                logger.log('[Registration] Verification result:', verificationResult);

                if (!verificationResult.success || !verificationResult.session) {
                    logger.error('[Registration] Payment verification failed:', verificationResult);
                    // Payment verification failed
                    setFirebaseError(verificationResult.error || strings.PAYMENT_PROCESSING_ERROR);
                    setSubmitStatus('error');
                    restoreFormFromStorage();
                    setIsProcessingPayment(false);
                    setIsLoading(false);
                    return;
                }

                const paymentStatus = verificationResult.session.paymentStatus;
                logger.log('[Registration] Payment status:', paymentStatus);

                // Check if payment was successful
                if (paymentStatus === 'paid' || paymentStatus === 'complete') {
                    logger.log('[Registration] Payment successful, requesting password re-entry...');
                    // Retrieve registration data from sessionStorage
                    // SECURITY: Password is NOT stored in sessionStorage - user will re-enter it
                    const savedFormData = sessionStorage.getItem('pendingRegistration');
                    const savedImageData = sessionStorage.getItem('pendingOrganizationImage');

                    if (savedFormData) {
                        try {
                            const registrationData = JSON.parse(savedFormData);

                            // Convert base64 image back to File if it exists
                            let imageFile: File | null = null;
                            if (savedImageData) {
                                try {
                                    const imageData = JSON.parse(savedImageData);
                                    if (imageData.base64 && imageData.fileName) {
                                        // Convert base64 to blob and then to File
                                        const byteCharacters = atob(imageData.base64.split(',')[ 1 ] || imageData.base64);
                                        const byteNumbers = new Array(byteCharacters.length);
                                        for (let i = 0; i < byteCharacters.length; i++) {
                                            byteNumbers[ i ] = byteCharacters.charCodeAt(i);
                                        }
                                        const byteArray = new Uint8Array(byteNumbers);
                                        const blob = new Blob([ byteArray ], { type: imageData.type || 'image/png' });
                                        imageFile = new File([ blob ], imageData.fileName, { type: imageData.type || 'image/png' });
                                    }
                                } catch (imageError) {
                                    logger.error('Error converting image data:', imageError);
                                }
                            }

                            // Get subscriptionId from checkout session if available
                            const subscriptionId = verificationResult.session.subscription || null;

                            // SECURITY: Show password re-entry modal instead of completing registration
                            // Password is NOT stored in sessionStorage for security reasons
                            // Set all states synchronously to prevent flickering
                            setPendingRegistrationData({
                                nickname: registrationData.nickname,
                                email: registrationData.email,
                                organization: registrationData.organization,
                                role: registrationData.role,
                                selectedPlan: registrationData.selectedPlan,
                                language: registrationData.language,
                                useDefaultLogo: registrationData.useDefaultLogo,
                                organizationImage: imageFile,
                                subscriptionId: subscriptionId
                            });
                            // Set modal state BEFORE clearing processing state to prevent flickering
                            setShowPasswordModal(true);
                            // Clear processing states after modal is set to prevent intermediate renders
                            setIsProcessingPayment(false);
                            setIsLoading(false);
                        } catch (error) {
                            logger.error('Error parsing saved registration data:', error);
                            setFirebaseError(strings.PAYMENT_PROCESSING_ERROR);
                            setSubmitStatus('error');
                            restoreFormFromStorage();
                            setIsProcessingPayment(false);
                            setIsLoading(false);
                        }
                    } else {
                        setFirebaseError(strings.PAYMENT_PROCESSING_ERROR);
                        setSubmitStatus('error');
                        setIsProcessingPayment(false);
                        setIsLoading(false);
                    }
                } else {
                    // Payment was not completed
                    setFirebaseError(strings.PAYMENT_NOT_COMPLETED);
                    setSubmitStatus('error');
                    restoreFormFromStorage();
                    setIsProcessingPayment(false);
                    setIsLoading(false);
                }
            }).catch((error) => {
                logger.error('Error verifying payment:', error);
                setFirebaseError(error.message || strings.PAYMENT_PROCESSING_ERROR);
                setSubmitStatus('error');
                restoreFormFromStorage();
                setIsProcessingPayment(false);
                setIsLoading(false);
            });
        } else if (paymentSuccess === 'false' || searchParams.get('cancelled') === 'true') {
            // Payment was cancelled
            setFirebaseError(strings.PAYMENT_CANCELLED);
            setSubmitStatus('error');
            restoreFormFromStorage();
            setIsProcessingPayment(false);
            setIsLoading(false);
        }
    }, [ searchParams, completeRegistration, strings ]);

    // Restore form data from sessionStorage when payment fails
    const restoreFormFromStorage = () => {
        const savedFormData = sessionStorage.getItem('pendingRegistration');
        if (savedFormData) {
            try {
                const registrationData = JSON.parse(savedFormData);
                setFormData({
                    nickname: registrationData.nickname || '',
                    email: registrationData.email || '',
                    password: '',
                    repassword: '',
                    organization: registrationData.organization || '',
                    role: registrationData.role || '',
                    selectedPlan: registrationData.selectedPlan || ''
                });

                // Restore logo selection
                if (registrationData.useDefaultLogo !== undefined) {
                    setUseDefaultLogo(registrationData.useDefaultLogo);
                }

                // Note: File cannot be restored from sessionStorage easily
                // User will need to select logo again if payment fails
            } catch (error) {
                logger.error('Error restoring form data:', error);
            }
        }
    };

    // Validate organization logo file type (only PNG and JPEG)
    const isValidLogoFile = (file: File | null): boolean => {
        if (!file) {
            return false;
        }
        const mimeType = file.type.toLowerCase();
        if (mimeType === 'image/png' || mimeType === 'image/jpeg') {
            return true;
        }
        const name = file.name.toLowerCase();
        return name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg');
    };

    // Create and cleanup preview URL for organization logo
    useEffect(() => {
        if (!organizationImage || !isValidLogoFile(organizationImage)) {
            setOrganizationImagePreview(null);
            return;
        }

        const objectUrl = URL.createObjectURL(organizationImage);
        setOrganizationImagePreview(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [ organizationImage ]);

    // Password validation with security best practices
    const validatePassword = (password: string): string | null => {
        if (!password.trim()) {
            return strings.REQUIRED_FIELD_ERROR;
        }

        if (password.length < 8) {
            return strings.PASSWORD_TOO_SHORT_ERROR;
        }

        if (password.length > 128) {
            return strings.PASSWORD_TOO_LONG_ERROR;
        }

        // Check for at least one uppercase letter
        if (!/[A-Z]/.test(password)) {
            return strings.PASSWORD_MISSING_UPPERCASE_ERROR;
        }

        // Check for at least one lowercase letter
        if (!/[a-z]/.test(password)) {
            return strings.PASSWORD_MISSING_LOWERCASE_ERROR;
        }

        // Check for at least one digit
        if (!/\d/.test(password)) {
            return strings.PASSWORD_MISSING_DIGIT_ERROR;
        }

        // Check for at least one special character
        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
            return strings.PASSWORD_MISSING_SPECIAL_CHAR_ERROR;
        }

        // Check for common weak patterns
        if (/(.)\1{2,}/.test(password)) {
            return strings.PASSWORD_REPEATED_CHARS_ERROR;
        }

        // Check for common sequences
        if (/123|abc|qwe|asd|zxc/i.test(password)) {
            return strings.PASSWORD_COMMON_SEQUENCE_ERROR;
        }

        return null; // Password is valid
    };

    const validateForm = (): boolean => {
        const newErrors: RegistrationErrors = {};

        if (!formData.nickname.trim()) {
            newErrors.nickname = strings.REQUIRED_FIELD_ERROR;
        }

        if (!formData.email.trim()) {
            newErrors.email = strings.REQUIRED_FIELD_ERROR;
        } else if (!validateEmail(formData.email)) {
            newErrors.email = strings.INVALID_EMAIL_ERROR;
        }

        const passwordError = validatePassword(formData.password);
        if (passwordError) {
            newErrors.password = passwordError;
        }

        if (!formData.repassword.trim()) {
            newErrors.repassword = strings.REQUIRED_FIELD_ERROR;
        } else if (formData.password !== formData.repassword) {
            newErrors.repassword = strings.PASSWORDS_DONT_MATCH_ERROR;
        }

        if (!formData.role.trim()) {
            newErrors.role = strings.REQUIRED_FIELD_ERROR;
        }

        const isOrganizationRequired = formData.role === 'manager';
        if (isOrganizationRequired && !formData.organization.trim()) {
            newErrors.organization = strings.REQUIRED_FIELD_ERROR;
        }

        const isPlanRequired = formData.role === 'manager';
        if (isPlanRequired && !formData.selectedPlan.trim()) {
            newErrors.selectedPlan = strings.REQUIRED_FIELD_ERROR;
        }

        // Organization logo is required and must be PNG or JPEG for manager role
        if (formData.role === 'manager') {
            if (!useDefaultLogo) {
                if (!organizationImage) {
                    newErrors.organizationLogo = strings.ORGANIZATION_LOGO_REQUIRED_ERROR;
                } else if (!isValidLogoFile(organizationImage)) {
                    newErrors.organizationLogo = strings.ORGANIZATION_LOGO_FORMAT_ERROR;
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: keyof RegistrationFormData, value: string) => {
        setFormData(prev => ({ ...prev, [ field ]: value }));

        // Clear error when user starts typing
        if (errors[ field ]) {
            setErrors(prev => ({ ...prev, [ field ]: undefined }));
        }
    };

    const handleBlur = (field: keyof RegistrationFormData) => {
        // Validate field on blur
        const newErrors: RegistrationErrors = { ...errors };

        if (field === 'nickname' && !formData.nickname.trim()) {
            newErrors.nickname = strings.REQUIRED_FIELD_ERROR;
        }

        if (field === 'email') {
            if (!formData.email.trim()) {
                newErrors.email = strings.REQUIRED_FIELD_ERROR;
            } else if (!validateEmail(formData.email)) {
                newErrors.email = strings.INVALID_EMAIL_ERROR;
            }
        }

        if (field === 'password') {
            const passwordError = validatePassword(formData.password);
            if (passwordError) {
                newErrors.password = passwordError;
            }
        }

        if (field === 'repassword') {
            if (!formData.repassword.trim()) {
                newErrors.repassword = strings.REQUIRED_FIELD_ERROR;
            } else if (formData.password !== formData.repassword) {
                newErrors.repassword = strings.PASSWORDS_DONT_MATCH_ERROR;
            }
        }

        if (field === 'role' && !formData.role.trim()) {
            newErrors.role = strings.REQUIRED_FIELD_ERROR;
        }

        if (field === 'organization' && formData.role === 'manager' && !formData.organization.trim()) {
            newErrors.organization = strings.REQUIRED_FIELD_ERROR;
        }

        setErrors(newErrors);
    };

    // Handle organization image file selection (for manager role)
    const handleOrganizationImageChange = (file: File | null) => {
        setOrganizationImage(file);
        // When user selects a custom logo, disable "use default logo"
        if (file) {
            setUseDefaultLogo(false);
        }
        if (errors.organizationLogo) {
            setErrors(prev => ({ ...prev, organizationLogo: undefined }));
        }
    };

    const handleUseDefaultLogoChange = (checked: boolean) => {
        setUseDefaultLogo(checked);
        if (checked) {
            // Clear custom logo and related errors when using default logo
            setOrganizationImage(null);
            if (errors.organizationLogo) {
                setErrors(prev => ({ ...prev, organizationLogo: undefined }));
            }
        }
    };

    // Handle password modal submission (after returning from Stripe payment)
    const handlePasswordModalSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setModalPasswordError('');

        // Validate password
        const passwordError = validatePassword(modalPassword);
        if (passwordError) {
            setModalPasswordError(passwordError);
            return;
        }

        // Validate password confirmation
        if (modalPassword !== modalRepassword) {
            setModalPasswordError(strings.PASSWORDS_DONT_MATCH_ERROR);
            return;
        }

        if (!pendingRegistrationData) {
            setModalPasswordError(strings.REGISTRATION_ERROR);
            return;
        }

        // Set modal loading state - keep modal visible during processing
        setModalLoading(true);
        setModalPasswordError('');

        try {
            // Register user with Firebase
            const authUser = await registerUser({
                nickname: pendingRegistrationData.nickname,
                email: pendingRegistrationData.email,
                password: modalPassword,
                organization: pendingRegistrationData.organization,
                role: pendingRegistrationData.role,
                selectedPlan: pendingRegistrationData.selectedPlan,
                language: pendingRegistrationData.language,
                organizationLogoFile: pendingRegistrationData.useDefaultLogo ? null : pendingRegistrationData.organizationImage,
                useDefaultLogo: pendingRegistrationData.useDefaultLogo
            });

            logger.log('User registered successfully:', authUser);

            // Save role before clearing data for redirect
            const userRole = pendingRegistrationData.role;

            // If subscriptionId is available, sync subscription status immediately
            if (pendingRegistrationData.subscriptionId && userRole === 'manager') {
                try {
                    logger.log('[Registration] Syncing subscription status after registration...', { subscriptionId: pendingRegistrationData.subscriptionId });
                    await syncSubscriptionStatus(pendingRegistrationData.subscriptionId);
                    logger.log('[Registration] Subscription status synced successfully');
                } catch (syncError) {
                    // Don't fail registration if sync fails - webhooks will update it later
                    logger.warn('[Registration] Failed to sync subscription status, webhooks will update it:', syncError);
                }
            }

            // Clear saved data after successful registration
            sessionStorage.removeItem('pendingRegistration');
            sessionStorage.removeItem('pendingOrganizationImage');

            // Set flag to prevent showing registration form before redirect
            // This prevents flickering to registration page
            setIsRegistrationComplete(true);

            // Clear sensitive data from state (but keep modal visible until redirect)
            setModalPassword('');
            setModalRepassword('');
            // Don't clear pendingRegistrationData yet - keep modal visible until redirect

            // Redirect only after all operations are complete
            const redirectPath = userRole === 'manager' ? '/dashboard' : '/home';
            window.location.replace(window.location.origin + redirectPath);
        } catch (error) {
            logger.error('Registration failed after password re-entry:', error);
            const authError = error as AuthErrorInfo;
            setModalPasswordError(getAuthErrorMessage(authError.code, strings));
            setModalLoading(false);
            // Keep modal open so user can retry
        }
    };

    // Cancel password modal and restore form
    const handlePasswordModalCancel = () => {
        setShowPasswordModal(false);
        setModalPassword('');
        setModalRepassword('');
        setModalPasswordError('');
        setPendingRegistrationData(null);
        restoreFormFromStorage();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setSubmitStatus('idle');
        setFirebaseError('');

        try {
            // Check if payment is required (manager role with basic or premium plan)
            const requiresPayment = formData.role === 'manager' &&
                (formData.selectedPlan === 'basic' || formData.selectedPlan === 'premium');

            if (requiresPayment) {
                logger.log('[Registration] Payment required for plan:', formData.selectedPlan);

                // Create Stripe checkout session
                const currentUrl = window.location.origin + window.location.pathname;
                const successUrl = `${currentUrl}?payment_success=true&session_id={CHECKOUT_SESSION_ID}`;
                const cancelUrl = `${currentUrl}?payment_success=false&cancelled=true`;

                logger.log('[Registration] Stripe URLs:', {
                    successUrl: successUrl,
                    cancelUrl: cancelUrl
                });

                // Save registration data to sessionStorage for after payment completion
                // SECURITY: Password is NEVER stored in sessionStorage - user will re-enter it after payment
                const registrationDataToSave = {
                    nickname: formData.nickname,
                    email: formData.email,
                    // SECURITY FIX: Password removed from sessionStorage to prevent exposure
                    organization: formData.organization,
                    role: formData.role,
                    selectedPlan: formData.selectedPlan,
                    language: language,
                    useDefaultLogo: useDefaultLogo
                };

                logger.log('[Registration] Saving registration data to sessionStorage (password excluded for security)');
                sessionStorage.setItem('pendingRegistration', JSON.stringify(registrationDataToSave));

                // Convert File to base64 for storage if exists
                if (organizationImage && !useDefaultLogo) {
                    logger.log('[Registration] Converting organization image to base64');
                    await new Promise<void>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            try {
                                const base64String = reader.result as string;
                                sessionStorage.setItem('pendingOrganizationImage', JSON.stringify({
                                    base64: base64String,
                                    fileName: organizationImage.name,
                                    type: organizationImage.type
                                }));
                                logger.log('[Registration] Organization image saved to sessionStorage');
                                resolve();
                            } catch (error) {
                                logger.error('[Registration] Error saving image:', error);
                                reject(error);
                            }
                        };
                        reader.onerror = () => {
                            logger.error('[Registration] FileReader error');
                            reject(new Error('Error reading organization image file'));
                        };
                        reader.readAsDataURL(organizationImage);
                    });
                }

                logger.log('[Registration] Calling createCheckoutSession with params:', {
                    planId: formData.selectedPlan,
                    customerEmail: formData.email,
                    metadata: {
                        email: formData.email,
                        nickname: formData.nickname,
                        organization: formData.organization,
                        plan: formData.selectedPlan
                    }
                });

                const checkoutResult = await createCheckoutSession({
                    planId: formData.selectedPlan,
                    customerEmail: formData.email,
                    successUrl: successUrl,
                    cancelUrl: cancelUrl,
                    metadata: {
                        email: formData.email,
                        nickname: formData.nickname,
                        organization: formData.organization,
                        plan: formData.selectedPlan
                    }
                });

                logger.log('[Registration] Checkout session result:', checkoutResult);

                if (checkoutResult.success && checkoutResult.url) {
                    logger.log('[Registration] Redirecting to Stripe checkout:', checkoutResult.url);
                    // Redirect to Stripe checkout
                    window.location.href = checkoutResult.url;
                    return; // Don't set loading to false yet, we're redirecting
                } else {
                    logger.error('[Registration] Checkout session creation failed:', checkoutResult.error);
                    throw new Error(checkoutResult.error || 'Failed to create checkout session');
                }
            } else {
                // No payment required - register directly
                await completeRegistration({
                    nickname: formData.nickname,
                    email: formData.email,
                    password: formData.password,
                    organization: formData.organization,
                    role: formData.role,
                    selectedPlan: formData.role === 'manager' ? formData.selectedPlan : undefined,
                    language: language,
                    organizationImage: organizationImage,
                    useDefaultLogo: useDefaultLogo
                });
            }
        } catch (err) {
            logger.error('[Registration] Registration/payment error:', err);
            logger.error('[Registration] Error details:', {
                message: err instanceof Error ? err.message : 'Unknown error',
                stack: err instanceof Error ? err.stack : undefined,
                name: err instanceof Error ? err.name : undefined
            });
            const errorMessage = err instanceof Error ? err.message : 'An error occurred';
            setFirebaseError(errorMessage);
            setSubmitStatus('error');
            setIsLoading(false);
        }
    };

    const isOrganizationRequired = formData.role === 'manager';
    const isPlanRequired = formData.role === 'manager';

    // Basic validation - check required fields
    const hasBasicFields = formData.nickname.trim() && formData.email.trim() && formData.password.trim() && formData.repassword.trim() && formData.role.trim();

    // Password match validation - only check if both fields have values and match
    const passwordsMatch = !formData.repassword.trim() || formData.password === formData.repassword;

    // Conditional validation for manager role
    const hasConditionalFields = !isOrganizationRequired || formData.organization.trim();
    const hasPlanSelection = !isPlanRequired || formData.selectedPlan.trim();
    const hasValidLogo = !isOrganizationRequired || useDefaultLogo || (organizationImage !== null && isValidLogoFile(organizationImage));

    const isFormValid = hasBasicFields && passwordsMatch && hasConditionalFields && hasPlanSelection && hasValidLogo;

    // Memoize conditions to prevent unnecessary recalculations and flickering
    const shouldShowPasswordModal = useMemo(() => {
        // Keep modal visible even after registration complete (until redirect) to prevent flickering
        return showPasswordModal && (pendingRegistrationData !== null || isRegistrationComplete);
    }, [ isRegistrationComplete, showPasswordModal, pendingRegistrationData ]);

    const shouldShowLoadingScreen = useMemo(() => {
        // Never show loading screen if password modal should be shown (even after registration complete)
        if (shouldShowPasswordModal) return false;
        // Show loading screen if registration is complete (prevent showing form before redirect)
        if (isRegistrationComplete) return true;
        return isProcessingPayment || (isReturningFromPayment && !showPasswordModal);
    }, [ isRegistrationComplete, shouldShowPasswordModal, isProcessingPayment, isReturningFromPayment, showPasswordModal ]);

    // Show password re-entry modal after returning from Stripe payment
    // SECURITY: This ensures password is never stored in sessionStorage
    // IMPORTANT: Check this FIRST to prevent flickering between screens
    if (shouldShowPasswordModal) {
        return (
            <div className="container">
                <LanguageSwitcher />
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h2>{strings.PAYMENT_SUCCESS_ENTER_PASSWORD_TITLE || 'Makse õnnestus'}</h2>
                        </div>
                        <div className="modal-body">
                            {isRegistrationComplete ? (
                                <EngineeringLoadingScreen
                                    variant="compact"
                                    ariaLabel={
                                        `${strings.REGISTERING_BUTTON || ''} ${strings.PLEASE_WAIT || ''}`.trim() ||
                                        strings.LOADING
                                    }
                                />
                            ) : (
                                <>
                                    <p className="password-modal-description">
                                        {strings.PAYMENT_SUCCESS_ENTER_PASSWORD_DESC || 'Palun sisestage oma parool registreerimise lõpetamiseks.'}
                                    </p>
                                    <form onSubmit={handlePasswordModalSubmit}>
                                        <label>
                                            {strings.PASSWORD_LABEL} *
                                            <input
                                                type="password"
                                                value={modalPassword}
                                                onChange={e => {
                                                    setModalPassword(e.target.value);
                                                    setModalPasswordError('');
                                                }}
                                                placeholder={strings.PASSWORD_PLACEHOLDER}
                                                autoFocus
                                                disabled={modalLoading}
                                            />
                                        </label>

                                        <label>
                                            {strings.CONFIRM_PASSWORD_LABEL} *
                                            <input
                                                type="password"
                                                value={modalRepassword}
                                                onChange={e => {
                                                    setModalRepassword(e.target.value);
                                                    setModalPasswordError('');
                                                }}
                                                placeholder={strings.CONFIRM_PASSWORD_PLACEHOLDER}
                                                disabled={modalLoading}
                                            />
                                        </label>

                                        {modalPasswordError && (
                                            <div className="error-message">
                                                {modalPasswordError}
                                            </div>
                                        )}

                                        <div className="modal-footer">
                                            <button
                                                type="button"
                                                className="btn-secondary"
                                                onClick={handlePasswordModalCancel}
                                                disabled={modalLoading}
                                            >
                                                {strings.PROFILE_CANCEL || 'Tühista'}
                                            </button>
                                            <button
                                                type="submit"
                                                className={`btn-active ${modalLoading || !modalPassword || !modalRepassword ? 'btn-active-disabled' : ''}`}
                                                disabled={modalLoading || !modalPassword || !modalRepassword}
                                            >
                                                {modalLoading ? (
                                                    <span className="button-loading-content">
                                                        <span className="button-spinner"></span>
                                                        <span>{strings.REGISTERING_BUTTON || 'Registreerimine...'}</span>
                                                    </span>
                                                ) : (
                                                    strings.COMPLETE_REGISTRATION_BUTTON || 'Lõpeta registreerimine'
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show loading screen when processing payment OR when returning from payment
    // But ONLY if password modal is NOT shown (to prevent flickering)
    if (shouldShowLoadingScreen) {
        return (
            <EngineeringLoadingScreen
                ariaLabel={
                    `${strings.PROCESSING_PAYMENT || ''} ${strings.PLEASE_WAIT || ''}`.trim() || strings.LOADING
                }
            />
        );
    }

    return (
        <div className="container">
            <LanguageSwitcher />
            <form onSubmit={handleSubmit}>
                <h3>{strings.APP_TITLE}</h3>
                {invitationInfo && (
                    <div className="invitation-info-banner">
                        ℹ️ {invitationInfo}
                    </div>
                )}

                <label>
                    {strings.USERNAME_LABEL} *
                    <input
                        type="text"
                        value={formData.nickname}
                        onChange={e => handleInputChange('nickname', e.target.value)}
                        onBlur={() => handleBlur('nickname')}
                        placeholder={strings.USERNAME_PLACEHOLDER}
                        className={errors.nickname ? 'input-error' : ''}
                    />
                    {errors.nickname && <span className="error-message">{errors.nickname}</span>}
                </label>

                <label>
                    {strings.EMAIL_LABEL} *
                    <input
                        type="email"
                        value={formData.email}
                        onChange={e => handleInputChange('email', e.target.value)}
                        onBlur={() => handleBlur('email')}
                        placeholder={strings.EMAIL_PLACEHOLDER}
                        className={errors.email ? 'input-error' : ''}
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                </label>

                <label>
                    {strings.PASSWORD_LABEL} *
                    <input
                        type="password"
                        value={formData.password}
                        onChange={e => handleInputChange('password', e.target.value)}
                        onBlur={() => handleBlur('password')}
                        placeholder={strings.PASSWORD_PLACEHOLDER}
                        className={errors.password ? 'input-error' : ''}
                    />
                    {errors.password && <span className="error-message">{errors.password}</span>}
                </label>

                <label>
                    {strings.CONFIRM_PASSWORD_LABEL} *
                    <input
                        type="password"
                        value={formData.repassword}
                        onChange={e => handleInputChange('repassword', e.target.value)}
                        onBlur={() => handleBlur('repassword')}
                        placeholder={strings.CONFIRM_PASSWORD_PLACEHOLDER}
                        className={errors.repassword ? 'input-error' : ''}
                    />
                    {errors.repassword && <span className="error-message">{errors.repassword}</span>}
                </label>

                <label>
                    {strings.ROLE_LABEL} *
                    <MaterialDropdown
                        options={[
                            // MANAGER REGISTRATION IS DISABLED FOR NOW
                            //{ value: 'manager', label: strings.ROLE1 },
                            { value: 'user', label: strings.ROLE2 }
                        ]}
                        value={formData.role}
                        onChange={(value) => handleInputChange('role', value)}
                        placeholder={strings.SELECT_ROLE_PLACEHOLDER}
                    />
                    {errors.role && <span className="error-message">{errors.role}</span>}
                </label>

                {formData.role === 'manager' && (
                    <>
                        <h1 className="pricing-plans-title" style={{ width: '100%', textAlign: 'center', color: '#1a365d' }}>
                            {strings.PRICING_PLANS_TITLE}
                        </h1>

                        <div className="pricing-plans-container">
                            <div className={`pricing-card ${formData.selectedPlan === 'basic' ? 'selected' : ''}`}>
                                <div className="pricing-card-header">
                                    <div className="pricing-card-title">{strings.BASIC_PLAN_TITLE}</div>
                                    <div className="pricing-card-price">60</div>
                                    <div className="pricing-card-period">{strings.MONTHLY_PRICE}</div>
                                </div>

                                <ul className="pricing-card-features">
                                    <li className="pricing-card-feature">
                                        <span className="pricing-card-feature-icon">✓</span>
                                        <span className="pricing-card-feature-text">
                                            <span className="pricing-card-feature-highlight">1</span> {strings.EDITORS_LABEL}
                                        </span>
                                    </li>
                                    <li className="pricing-card-feature">
                                        <span className="pricing-card-feature-icon">✓</span>
                                        <span className="pricing-card-feature-text">
                                            <span className="pricing-card-feature-highlight">5</span> {strings.USERS_LABEL}
                                        </span>
                                    </li>
                                    <li className="pricing-card-feature">
                                        <span className="pricing-card-feature-icon">✓</span>
                                        <span className="pricing-card-feature-text">
                                            <span className="pricing-card-feature-highlight">3</span> {strings.MEASUREMENT_TOOLS_LABEL}
                                        </span>
                                    </li>
                                    <li className="pricing-card-feature">
                                        <span className="pricing-card-feature-text pricing-card-feature-comment">
                                            <span className="pricing-card-feature-highlight pricing-card-feature-comment">5</span> {strings.ADDITIONAL_TOOL_PRICE}
                                        </span>
                                    </li>
                                </ul>

                                <button
                                    type="button"
                                    className="pricing-card-button"
                                    onClick={() => handleInputChange('selectedPlan', 'basic')}
                                >
                                    {formData.selectedPlan === 'basic' ? '✓ ' : ''}{strings.SELECT_PLAN_BUTTON}
                                </button>
                            </div>

                            <div className={`pricing-card ${formData.selectedPlan === 'premium' ? 'selected' : ''}`}>
                                <div className="pricing-card-header">
                                    <div className="pricing-card-title">{strings.PREMIUM_PLAN_TITLE}</div>
                                    <div className="pricing-card-price">240</div>
                                    <div className="pricing-card-period">{strings.MONTHLY_PRICE}</div>
                                </div>

                                <ul className="pricing-card-features">
                                    <li className="pricing-card-feature">
                                        <span className="pricing-card-feature-icon">✓</span>
                                        <span className="pricing-card-feature-text">
                                            <span className="pricing-card-feature-highlight">5</span> {strings.EDITORS_LABEL}
                                        </span>
                                    </li>
                                    <li className="pricing-card-feature">
                                        <span className="pricing-card-feature-icon">✓</span>
                                        <span className="pricing-card-feature-text">
                                            <span className="pricing-card-feature-highlight">30</span> {strings.USERS_LABEL}
                                        </span>
                                    </li>
                                    <li className="pricing-card-feature">
                                        <span className="pricing-card-feature-icon">✓</span>
                                        <span className="pricing-card-feature-text">
                                            <span className="pricing-card-feature-highlight">{getSubscriptionLimits('premium').toolLimit === 'unlimited' ? strings.UNLIMITED_LABEL : getSubscriptionLimits('premium').toolLimit}</span> {strings.MEASUREMENT_TOOLS_LABEL}
                                        </span>
                                    </li>
                                    <li className="pricing-card-feature">
                                        <span className="pricing-card-feature-text pricing-card-feature-comment">
                                            <span className="pricing-card-feature-highlight pricing-card-feature-comment">5</span> {strings.ADDITIONAL_TOOL_PRICE}
                                        </span>
                                    </li>
                                </ul>

                                <button
                                    type="button"
                                    className="pricing-card-button"
                                    onClick={() => handleInputChange('selectedPlan', 'premium')}
                                >
                                    {formData.selectedPlan === 'premium' ? '✓ ' : ''}{strings.SELECT_PLAN_BUTTON}
                                </button>
                            </div>

                            <div className={`pricing-card ${formData.selectedPlan === 'ultimate' ? 'selected' : ''}`}>
                                <div className="pricing-card-header">
                                    <div className="pricing-card-title">{strings.ULTIMATE_PLAN_TITLE}</div>
                                    <div className="pricing-card-price">-</div>
                                    <div className="pricing-card-period">{strings.MONTHLY_PRICE}</div>
                                </div>

                                <ul className="pricing-card-features">
                                    <li className="pricing-card-feature">
                                        <span className="pricing-card-feature-icon">✓</span>
                                        <span className="pricing-card-feature-text">
                                            <span className="pricing-card-feature-highlight">?</span> {strings.EDITORS_LABEL}
                                        </span>
                                    </li>
                                    <li className="pricing-card-feature">
                                        <span className="pricing-card-feature-icon">✓</span>
                                        <span className="pricing-card-feature-text">
                                            <span className="pricing-card-feature-highlight">?</span> {strings.USERS_LABEL}
                                        </span>
                                    </li>
                                    <li className="pricing-card-feature">
                                        <span className="pricing-card-feature-icon">✓</span>
                                        <span className="pricing-card-feature-text">
                                            <span className="pricing-card-feature-highlight">?</span> {strings.MEASUREMENT_TOOLS_LABEL}
                                        </span>
                                    </li>
                                </ul>

                                <button
                                    type="button"
                                    className="pricing-card-button-contact"
                                    onClick={onContactUs}
                                >
                                    {strings.CONTACT_US_BUTTON}
                                </button>
                            </div>
                        </div>

                        <label>
                            {strings.ORGANIZATION_LABEL} *
                            <input
                                type="text"
                                value={formData.organization}
                                onChange={e => handleInputChange('organization', e.target.value)}
                                onBlur={() => handleBlur('organization')}
                                placeholder={strings.ORGANIZATION_PLACEHOLDER}
                                className={errors.organization ? 'input-error' : ''}
                            />
                            {errors.organization && <span className="error-message">{errors.organization}</span>}
                        </label>

                        <div className="organization-logo-header-row">
                            <span className="organization-logo-title-text">
                                {strings.ORGANIZATION_IMAGE_LABEL}
                            </span>
                            <span className="organization-logo-or-text">
                                {strings.OR_LABEL}
                            </span>
                            <div className="organization-logo-default-row">
                                <span className="organization-logo-title-text">
                                    {strings.USE_DEFAULT_LOGO_LABEL}
                                </span>
                                <input
                                    type="checkbox"
                                    checked={useDefaultLogo}
                                    onChange={e => handleUseDefaultLogoChange(e.target.checked)}
                                />
                            </div>
                        </div>
                        <div className="organization-logo-row">
                            <label className="organization-logo-label">
                                <div className="organization-logo-input-wrapper">
                                    <input
                                        ref={organizationLogoInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="organization-logo-input-hidden"
                                        onChange={e =>
                                            handleOrganizationImageChange(
                                                e.target.files && e.target.files.length > 0
                                                    ? e.target.files[ 0 ]
                                                    : null
                                            )
                                        }
                                    />
                                    {!useDefaultLogo && (
                                        <button
                                            type="button"
                                            className="organization-logo-icon-button"
                                            onClick={() => organizationLogoInputRef.current?.click()}
                                        >
                                            {organizationImage ? (
                                                organizationImage.name
                                            ) : (
                                                <i className="fa-regular fa-image" aria-hidden="true" />
                                            )}
                                        </button>
                                    )}
                                    {errors.organizationLogo && (
                                        <span className="error-message">{errors.organizationLogo}</span>
                                    )}
                                </div>
                            </label>
                            {organizationImagePreview && (
                                <div className="organization-logo-preview">
                                    <img src={organizationImagePreview} alt={strings.ORGANIZATION_LOGO_PREVIEW} />
                                </div>
                            )}
                        </div>

                        {errors.selectedPlan && (
                            <div className="error-message">
                                {errors.selectedPlan}
                            </div>
                        )}
                    </>
                )}

                {/* Don't show success/error messages when processing payment */}
                {!isProcessingPayment && submitStatus === 'success' && (
                    <div className="success-message">
                        {strings.REGISTRATION_SUCCESS}
                    </div>
                )}

                {!isProcessingPayment && submitStatus === 'error' && (
                    <div className="error-message">
                        {firebaseError || strings.REGISTRATION_ERROR}
                    </div>
                )}

                <div className="row-container">
                    <button
                        type="submit"
                        disabled={isLoading || !isFormValid}
                        className={`btn-active ${isLoading || !isFormValid ? 'btn-active-disabled' : ''}`}
                    >
                        {isLoading ? strings.REGISTERING_BUTTON : strings.REGISTER_BUTTON}
                    </button>
                </div>

                <div className="btn-back">
                    <button
                        type="button"
                        className="btn-underline"
                        onClick={onBackToLogin}
                    >
                        {strings.BACK_BUTTON}
                    </button>
                </div>
            </form>
        </div>
    );
}
